<template>
  <div>
    <Head title="Campaigns" />
    <h1 class="mb-8 font-bold text-3xl w-full"> Campaigns
    </h1>
    <div class="flex flex-wrap items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md w-full" @reset="reset">
        <div class="pb-4">
          <label class="block text-gray-700 font-bold">Target:</label>
          <select v-model="form.audianceFilter" class="mt-1 w-full form-select">
            <option :value="null" />
            <option value="leads">Leads</option>
            <option value="contacts">Contacts</option>
          </select>
        </div>
        <div class="pb-4">
          <label class="block text-gray-700 font-bold">Shared:</label>
          <select v-model="form.sharedFilter" class="mt-1 w-full form-select">
            <option :value="null" />
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        </div>
      </search-filter>
      <div class="inline-flex gap-2 py-4 flex flex-wrap justify-between gap-2">
        <CampaignAdd :auth="auth.user" />
      </div>
    </div>
    <div v-for="(campaign, index) in campaigns.data" :key="campaign.id">
      <div class="max-w-screen-xl m-auto">
        <div class="w-full text-white">
          <div class="flex justify-center">
            <span v-if="index > 0" class="py-6" />
          </div>
        </div>
        <div class="bg-white rounded-3xl shadow overflow-x-auto">
          <div :id="campaign.id" class="w-full flex flex-wrap text-center">
            <div class="w-full py-3 px-6">
              <div class="py-4 lg:flex lg:justify-between">
                <div class="text-center w-full">
                  <span :class="{ 'text-gray-800': campaign.shared, 'text-indigo-600': !campaign.shared }" class="text-2xl font-bold text-center">
                    {{ campaign.name }}
                  </span>
                </div>
                <div class="text-xl w-full">
                  <span class="text-xl font-bold text-center text-indigo-600">
                    Target: {{ campaign.target }}
                  </span>
                </div>
              </div>
              <div class="w-full text-lg">
                <div><span class="font-bold"></span>{{ campaign.description }}</div>
              </div>
            </div>
            <div class="w-full flex py-3 px-6 items-center">
              <div class="hidden md:block mr-auto ml-auto text-sm font-normal text-center text-gray-600">
                <span v-if="campaign.shared" class="mr-10"><i class="fas fa-retweet"></i> Shared</span>
                <span v-if="campaign.user_id == auth.user.id" class="mr-10"><i class="far fa-user"></i> My Campaign</span>
                <span v-if="campaign.stop_on_reply" class="mr-10"><i class="fas fa-ban"></i> Stop on Reply</span>
                <span v-if="campaign.remove_on_status_change" class="mr-10"><i class="fas fa-unlink"></i> Status Change Remove</span>
                <span><i class="fas fa-list-ol"></i> {{ campaign.automation_count }} Automations</span>
              </div>
              <div class="ml-auto">
                <Link :href="`/campaigns/${campaign.id}/edit`">
                  <button
                    class="cursor-pointer inline-flex items-right px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-gray-600">
                    <span v-if="(campaign.shared && campaign.user_id == auth.user.id) || !campaign.shared || auth.user.owner">View Steps</span>
                    <span v-if="(campaign.shared && !auth.user.owner) && (campaign.shared && campaign.user_id == !auth.user.owner) || (campaign.shared && campaign.user_id !== auth.user.id && !auth.user.owner)">View Automations </span>
                  </button>
                </Link>
              </div>
              <CampaignEdit :auth="auth" :campaign="campaign" class="ml-4">
                <template #buttonTop>
                  <button class="cursor-pointer order-last place-items-end inline-flex items-right px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-400 hover:bg-gray-800" @click="openModal()">
                    <span v-if="(campaign.shared && campaign.user_id == auth.user.id) || !campaign.shared || auth.user.owner">Settings </span>
                    <span v-if="(campaign.shared && !auth.user.owner) && (campaign.shared && campaign.user_id == !auth.user.owner) || (campaign.shared && campaign.user_id !== auth.user.id && !auth.user.owner)">Settings </span>
                  </button>
                </template>
              </CampaignEdit>
              <div class="ml-4">
                <button class="cursor-pointer order-last place-items-end inline-flex items-right px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-400 hover:bg-gray-800" @click="duplicate(campaign.id)"> Duplicate </button>
              </div>
              <div class="ml-4" v-if="(campaign.shared && campaign.user_id == auth.user.id) || !campaign.shared || auth.user.owner">
                <button class="cursor-pointer inline-flex items-right px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-600 hover:bg-gray-800" @click="destroyCampaign(campaign.id)"> Delete </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="campaigns.length < 1">
      <div class="bg-white rounded-lg shadow overflow-x-auto">
        <div class="w-full flex flex-wrap text-center">
          <div class="w-full py-3 px-6">
            <div class="py-4 whitespace-nowrap">
              <h1 class="font-bold font-gray-800 text-3xl w-full text-center justify-center">No Campaigns
              </h1>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap justify-between align-center">
      <div class="pb-4 mt-6">
        <label class="block text-gray-700 font-bold">Per Page:</label>
        <select v-model="form.perPageFilter" class="mt-1 form-select">
          <option :value="null" />
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="200">200</option>
          <option value="250">250</option>
          <option value="500">500</option>
        </select>
      </div>
      <pagination class="mt-6" :links="campaigns.links" />
    </div>
  </div>
</template>

<script>
import { Head, Link, router } from '@inertiajs/vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import moment from 'moment'
import CampaignAdd from '@/Shared/CampaignAdd'
import CampaignEdit from '@/Shared/CampaignEdit'
import Pagination from '@/Shared/Pagination'
import { watch } from 'vue'

export default {
  components: {
    Head,
    Link,
    SearchFilter,
    Pagination,
    CampaignAdd,
    CampaignEdit,
  },
  layout: Layout,
  props: {
    auth: Object,
    filters: Object,
    campaigns: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        router.get('/campaigns', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    timestamp: function (date) {
      return moment(date).format('h:mm A')
    },
    datestamp: function (date) {
      return moment(date).format('MM/DD/YY')
    },
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    duplicate(id) {
      if (confirm('Would you like to duplicate this campaign?')) {
        router.post(('/campaigns/' + id + '/duplicate'))
      }
    },
    destroyCampaign(id) {
      if (confirm('Are you sure you want to delete this campaign?')) {
        router.delete(('/campaigns/' + id), { only: ['campaigns'], preserveScroll: true })
      }
    },
  },
}
</script>
